import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

import type { CohortFrequency, CohortGrid, CohortSource } from '../models/cohort.types'
import { buildCohortGrid, cohortMetrics } from '../service/cohort/cohortGrid'
import { scaleCohortGrid } from '../service/cohort/cohortScale'

import {
  useReportData,
  useReportSchedules,
  type ReportDataState,
  type ReportSpec,
  type ScheduleTab,
} from '@/features/reports'

const DIMENSIONS = ['cohort_period', 'cohort_type', 'metric_name', 'platform', 'start_date']
const DEFAULT_METRICS = ['total_revenue', 'mmp_sessions']

function firstScheduleTab(value: unknown): ScheduleTab | null {
  const first = Array.isArray(value) ? value[0] : value
  return typeof first === 'object' && first !== null ? first : null
}

export interface UseAcquisitionCohort {
  source: Ref<CohortSource>
  frequency: Ref<CohortFrequency>
  platform: Ref<string>
  metric: Ref<string>
  sources: ComputedRef<CohortSource[]>
  frequencies: ComputedRef<CohortFrequency[]>
  platforms: ComputedRef<string[]>
  metrics: ComputedRef<string[]>
  grid: ComputedRef<CohortGrid>
  state: Ref<ReportDataState>
  reload: () => Promise<void>
}

export function useAcquisitionCohort(): UseAcquisitionCohort {
  const schedules = useReportSchedules()
  const source = ref<CohortSource>('standard')
  const frequency = ref<CohortFrequency>('month')
  const platform = ref('')
  const metric = ref(DEFAULT_METRICS[0] ?? '')

  const standardSchedule = computed(() =>
    firstScheduleTab(schedules.checkScheduleTabs(18, ['month', 'week'])),
  )
  const customSchedule = computed(() =>
    firstScheduleTab(schedules.checkScheduleTabs(59, 'custom_mmp_cohort_data')),
  )
  const sources = computed<CohortSource[]>(() => [
    ...(standardSchedule.value ? (['standard'] as const) : []),
    ...(customSchedule.value ? (['custom'] as const) : []),
  ])
  const frequencies = computed<CohortFrequency[]>(() =>
    (['month', 'week'] as const).filter((value) => Boolean(schedules.checkScheduleTabs(18, value))),
  )
  const scheduleId = computed(() => schedules.getScheduleByReportId(18)?.id)
  const platforms = computed(() => {
    const id = scheduleId.value
    if (id === undefined) return []
    return schedules.dropdownableDimensions(id, 'platform')?.values ?? []
  })
  const spec = computed<ReportSpec | null>(() => {
    const reportId = source.value === 'standard' ? 18 : 59
    const partition = source.value === 'standard' ? 'mmp_cohort_data' : 'custom_mmp_cohort_data'
    if (source.value === 'standard' && !standardSchedule.value) return null
    if (source.value === 'custom' && !customSchedule.value) return null
    return {
      report_id: reportId,
      partition_name: partition,
      dimensions: DIMENSIONS,
      filters: [
        {
          field: 'cohort_type',
          match_mode: 'equals',
          value:
            source.value === 'standard'
              ? frequency.value
              : frequency.value === 'month'
                ? 'monthly'
                : 'weekly',
        },
        ...(platform.value
          ? [{ field: 'platform', match_mode: 'equals' as const, value: platform.value }]
          : []),
      ],
      pagination_enabled: false,
    }
  })
  const data = useReportData(spec)
  const metrics = computed(() => {
    const discovered = cohortMetrics(data.rows.value)
    if (discovered.length === 0) return DEFAULT_METRICS
    // v3 seeded the picker with Revenue, Session and appended whatever else the data carried.
    return [
      ...DEFAULT_METRICS.filter((value) => discovered.includes(value)),
      ...discovered.filter((value) => !DEFAULT_METRICS.includes(value)),
    ]
  })
  const grid = computed(() => scaleCohortGrid(buildCohortGrid(data.rows.value, metric.value)))

  watch(
    platforms,
    (values) => {
      if (!values.includes(platform.value)) platform.value = values[0] ?? ''
    },
    { immediate: true },
  )
  watch(
    frequencies,
    (values) => {
      if (!values.includes(frequency.value)) frequency.value = values[0] ?? 'month'
    },
    { immediate: true },
  )
  watch(
    sources,
    (values) => {
      if (!values.includes(source.value)) source.value = values[0] ?? 'standard'
    },
    { immediate: true },
  )
  watch(
    metrics,
    (values) => {
      if (!values.includes(metric.value)) metric.value = values[0] ?? ''
    },
    { immediate: true },
  )

  return {
    source,
    frequency,
    platform,
    metric,
    sources,
    frequencies,
    platforms,
    metrics,
    grid,
    state: data.state,
    reload: data.reload,
  }
}
